import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes, faCheck, faCheckDouble, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import './NotificationBell.css';
import { API_ENDPOINTS } from '../../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const NotificationBell = ({ onNavigateToNotifications }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [pollInterval, setPollInterval] = useState(240000); // Poll every 4 minutes
  const [rateLimitRetryCount, setRateLimitRetryCount] = useState(0);

  // BroadcastChannel for cross-tab communication
  const [broadcastChannel] = useState(() => {
    try {
      return new BroadcastChannel('notification-updates');
    } catch (e) {
      console.warn('BroadcastChannel not supported');
      return null;
    }
  });

  // Listen for updates from other tabs
  useEffect(() => {
    if (broadcastChannel) {
      const handleMessage = (event) => {
        if (event.data.type === 'unread-count-update') {
          setUnreadCount(event.data.count);
        }
      };
      broadcastChannel.addEventListener('message', handleMessage);
      return () => {
        broadcastChannel.removeEventListener('message', handleMessage);
        broadcastChannel.close();
      };
    }
  }, [broadcastChannel]);

  // Visibility change handler - pause polling when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible, fetch immediately
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Poll for unread count with adaptive interval
  useEffect(() => {
    fetchUnreadCount();
    
    const interval = setInterval(() => {
      // Only poll if tab is visible
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    }, pollInterval);
    
    return () => clearInterval(interval);
  }, [pollInterval]);

  const fetchUnreadCount = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      const response = await axios.get(
        API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        const newCount = response.data.unread_count;
        setUnreadCount(newCount);
        
        // Broadcast to other tabs
        if (broadcastChannel) {
          broadcastChannel.postMessage({
            type: 'unread-count-update',
            count: newCount
          });
        }
        
        // Reset rate limit counter and interval on success
        if (rateLimitRetryCount > 0) {
          setRateLimitRetryCount(0);
          setPollInterval(240000); // Reset to 4 minutes
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 429) {
        // Rate limit hit - implement exponential backoff
        const newRetryCount = rateLimitRetryCount + 1;
        setRateLimitRetryCount(newRetryCount);
        
        // Exponential backoff: 2min, 5min, 10min, max 15min
        const backoffIntervals = [120000, 300000, 600000, 900000];
        const newInterval = backoffIntervals[Math.min(newRetryCount - 1, backoffIntervals.length - 1)];
        setPollInterval(newInterval);
        
        console.warn(`Rate limit exceeded. Backing off to ${newInterval / 1000}s polling interval.`);
      } else {
        console.error('Error fetching unread count:', error);
      }
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      setLoading(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      // Fetch only unread notifications for the bell dropdown
      const response = await axios.get(
        `${API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS}?limit=20&unread_only=true`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!showModal) {
      fetchRecentNotifications();
    }
    setShowModal(!showModal);
  };

  const markAsRead = async (notificationId) => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      await axios.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId),
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Remove the notification from the list (since we only show unread)
      setNotifications(prev =>
        prev.filter(n => n.notification_id !== notificationId)
      );
      
      // Update unread count
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      await axios.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Clear all notifications from the list
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#dc3545';
      case 'medium':
        return '#fd7e14';
      default:
        return '#6c757d';
    }
  };

  const getActionDescription = (notification) => {
    const fields = notification.changed_fields || [];
    const collection = notification.collection_name || '';
    
    // Contract-related actions (most common/important)
    if (fields.includes('is_signed') || fields.includes('client_signature')) {
      return 'Signed their contract';
    }
    if (fields.includes('first_viewed_at')) {
      return 'Opened their contract for the first time';
    }
    if (fields.includes('sent_for_signature') || fields.includes('is_contractout')) {
      return 'Contract sent for signature';
    }
    if (fields.includes('sent_for_signature_at')) {
      return 'Contract sent to client';
    }
    
    // Monetization changes
    if (fields.includes('Snap_Monetized')) {
      return 'Monetization status changed';
    }
    if (fields.includes('Snap_Star')) {
      return 'Snap Star status changed';
    }
    
    // Payment information
    if (fields.some(f => ['client_payout_percent', 'company_net_percent'].includes(f))) {
      return 'Payment split updated';
    }
    if (fields.some(f => ['payment_method', 'payee_name'].includes(f))) {
      return 'Payment details updated';
    }
    
    // Profile changes
    if (fields.some(f => ['First_Legal_Name', 'Last_Legal_Name'].includes(f))) {
      return 'Legal name updated';
    }
    if (fields.includes('Stage_Name')) {
      return 'Stage name updated';
    }
    
    // Contact information
    if (fields.includes('Phone_Number') || fields.includes('Phone')) {
      return 'Phone number updated';
    }
    if (fields.includes('Email_Address')) {
      return 'Email address updated';
    }
    
    // Assignment changes
    if (fields.includes('assigned_employees')) {
      return 'Team assignment changed';
    }
    
    // Social media updates
    if (fields.some(f => f.includes('IG_'))) {
      if (fields.includes('IG_Username')) return 'Instagram username updated';
      if (fields.includes('IG_Followers')) return 'Instagram followers updated';
      if (fields.includes('IG_Verified')) return 'Instagram verified';
      return 'Instagram profile updated';
    }
    if (fields.some(f => f.includes('TT_'))) {
      if (fields.includes('TT_Username')) return 'TikTok username updated';
      if (fields.includes('TT_Followers')) return 'TikTok followers updated';
      if (fields.includes('TT_Verified')) return 'TikTok verified';
      return 'TikTok profile updated';
    }
    if (fields.some(f => f.includes('YT_'))) {
      if (fields.includes('YT_Username')) return 'YouTube username updated';
      if (fields.includes('YT_Followers')) return 'YouTube subscribers updated';
      if (fields.includes('YT_Verified')) return 'YouTube verified';
      return 'YouTube profile updated';
    }
    if (fields.some(f => f.includes('Snap_'))) {
      if (fields.includes('Snap_Username')) return 'Snapchat username updated';
      if (fields.includes('Snap_Followers')) return 'Snapchat followers updated';
      return 'Snapchat profile updated';
    }
    
    // Collection-specific fallbacks
    if (collection === 'Contracts') {
      return 'Contract updated';
    }
    if (collection === 'PaymentInfo') {
      return 'Payment information updated';
    }
    if (collection === 'SocialMedia') {
      return 'Social media updated';
    }
    if (collection === 'ClientInfo') {
      return 'Client information updated';
    }
    
    // Generic fallback - clean up field names
    const cleanFieldName = (field) => {
      return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase();
    };
    
    const displayFields = fields
      .filter(f => !f.includes('.'))  // Skip nested fields
      .slice(0, 2)
      .map(cleanFieldName)
      .join(' and ');
    
    return displayFields ? `Updated ${displayFields}` : 'Profile updated';
  };

  // Get category for grouping notifications
  const getActionCategory = (notification) => {
    const fields = notification.changed_fields || [];
    const collection = notification.collection_name || '';
    
    // Group all contract-related actions together
    if (collection === 'Contracts' || 
        fields.some(f => ['is_signed', 'client_signature', 'first_viewed_at', 'sent_for_signature', 'is_contractout', 'sent_for_signature_at'].includes(f))) {
      return 'contract';
    }
    
    // Group payment actions
    if (collection === 'PaymentInfo' || 
        fields.some(f => ['client_payout_percent', 'company_net_percent', 'payment_method', 'payee_name'].includes(f))) {
      return 'payment';
    }
    
    // Group profile actions
    if (fields.some(f => ['First_Legal_Name', 'Last_Legal_Name', 'Stage_Name'].includes(f))) {
      return 'profile';
    }
    
    // Group contact actions
    if (fields.some(f => ['Phone_Number', 'Phone', 'Email_Address'].includes(f))) {
      return 'contact';
    }
    
    // Group social media by platform
    if (fields.some(f => f.includes('IG_'))) return 'instagram';
    if (fields.some(f => f.includes('TT_'))) return 'tiktok';
    if (fields.some(f => f.includes('YT_'))) return 'youtube';
    if (fields.some(f => f.includes('Snap_'))) return 'snapchat';
    
    return 'other';
  };

  // Group notifications by client and category
  const groupNotifications = (notificationList) => {
    if (!notificationList || notificationList.length === 0) return [];
    
    const grouped = [];
    let currentGroup = null;
    
    notificationList.forEach((notif) => {
      const category = getActionCategory(notif);
      const clientId = notif.client_id;
      
      // Check if this notification belongs to the current group
      // Group if: same client AND same category
      if (currentGroup && 
          currentGroup.clientId === clientId && 
          currentGroup.category === category) {
        currentGroup.notifications.push(notif);
        currentGroup.count++;
      } else {
        // Start a new group
        if (currentGroup) {
          grouped.push(currentGroup);
        }
        currentGroup = {
          clientId: clientId,
          clientName: notif.client_name,
          category: category,
          action: getActionDescription(notif),
          notifications: [notif],
          count: 1,
          priority: notif.priority,
          timestamp: notif.timestamp
        };
      }
    });
    
    // Add the last group
    if (currentGroup) {
      grouped.push(currentGroup);
    }
    
    return grouped;
  };

  return (
    <>
      <div className="notification-bell-container">
        <button
          className="notification-bell-button"
          onClick={handleBellClick}
        >
          <FontAwesomeIcon icon={faBell} />
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      </div>

      {showModal && ReactDOM.createPortal(
        <div 
          className="notification-modal-overlay" 
          onClick={() => setShowModal(false)}
          style={{ display: 'block' }}
        >
          <div 
            className="notification-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex' }}
          >
            <div className="notification-modal-header">
              <h3>Notifications</h3>
              <div className="notification-modal-actions">
                {unreadCount > 0 && (
                  <button
                    className="mark-all-read-btn"
                    onClick={markAllAsRead}
                    title="Mark all as read"
                  >
                    <FontAwesomeIcon icon={faCheckDouble} />
                  </button>
                )}
                <button
                  className="close-modal-btn"
                  onClick={() => setShowModal(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="notification-modal-body">
              {loading ? (
                <div className="notification-loading">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notification-empty">
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="notification-list">
                  {groupNotifications(notifications).map((group, groupIndex) => (
                    <div key={groupIndex} className="notification-group">
                      {group.count > 1 ? (
                        <>
                          <div 
                            className="notification-group-header"
                            onClick={() => {
                              const newExpanded = new Set(expandedGroups);
                              if (newExpanded.has(groupIndex)) {
                                newExpanded.delete(groupIndex);
                              } else {
                                newExpanded.add(groupIndex);
                              }
                              setExpandedGroups(newExpanded);
                            }}
                          >
                            <div className="notification-priority-indicator"
                              style={{ backgroundColor: getPriorityColor(group.priority) }}
                            />
                            <div className="notification-content">
                              <div className="notification-group-summary">
                                <span className="notification-client-name">{group.clientName}</span>
                                <span className="notification-action">{group.action}</span>
                                {group.count > 1 && (
                                  <span className="notification-group-count">({group.count} updates)</span>
                                )}
                              </div>
                              <div className="notification-timestamp">
                                {formatTimestamp(group.timestamp)}
                              </div>
                            </div>
                            <button className="expand-btn">
                              <FontAwesomeIcon icon={expandedGroups.has(groupIndex) ? faChevronUp : faChevronDown} />
                            </button>
                          </div>
                          {expandedGroups.has(groupIndex) && (
                            <div className="notification-group-items">
                              {group.notifications.map((notif) => (
                                <div
                                  key={notif.notification_id}
                                  className={`notification-item ${notif.is_read ? 'read' : 'unread'}`}
                                >
                                  <div className="notification-priority-indicator"
                                    style={{ backgroundColor: getPriorityColor(notif.priority) }}
                                  />
                                  <div className="notification-content">
                                    <div className="notification-header-row">
                                      <span className="notification-client-name">
                                        {notif.client_name}
                                      </span>
                                      <span className="notification-timestamp">
                                        {formatTimestamp(notif.timestamp)}
                                      </span>
                                    </div>
                                    <div className="notification-action">
                                      {getActionDescription(notif)}
                                    </div>
                                    <div className="notification-actor">
                                      by {notif.actor_name}
                                    </div>
                                  </div>
                                  {!notif.is_read && (
                                    <button
                                      className="mark-read-btn"
                                      onClick={() => markAsRead(notif.notification_id)}
                                      title="Mark as read"
                                    >
                                      <FontAwesomeIcon icon={faCheck} />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          key={group.notifications[0].notification_id}
                          className={`notification-item ${group.notifications[0].is_read ? 'read' : 'unread'}`}
                        >
                          <div className="notification-priority-indicator"
                            style={{ backgroundColor: getPriorityColor(group.notifications[0].priority) }}
                          />
                          <div className="notification-content">
                            <div className="notification-header-row">
                              <span className="notification-client-name">
                                {group.notifications[0].client_name}
                              </span>
                              <span className="notification-timestamp">
                                {formatTimestamp(group.notifications[0].timestamp)}
                              </span>
                            </div>
                            <div className="notification-action">
                              {getActionDescription(group.notifications[0])}
                            </div>
                            <div className="notification-actor">
                              by {group.notifications[0].actor_name}
                            </div>
                          </div>
                          {!group.notifications[0].is_read && (
                            <button
                              className="mark-read-btn"
                              onClick={() => markAsRead(group.notifications[0].notification_id)}
                              title="Mark as read"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="notification-modal-footer">
              <button
                className="view-all-btn"
                onClick={() => {
                  setShowModal(false);
                  if (onNavigateToNotifications) {
                    onNavigateToNotifications();
                  }
                }}
              >
                View All Notifications
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;

